import { useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Leaf } from "lucide-react";
import Navigation from "@/components/site/Navigation";
import Footer from "@/components/site/Footer";
import Lightbox from "@/components/site/Lightbox";
import { FadeUp } from "@/components/site/Reveal";
import { getGathering, getAdjacent } from "@/data/gatherings";
import { useReserve } from "@/lib/useSectionNav";

const EASE = [0.22, 1, 0.36, 1];

export default function GatheringDetail() {
  const { slug } = useParams();
  const gathering = getGathering(slug);
  const [lightbox, setLightbox] = useState(null);
  const reserve = useReserve();

  if (!gathering) return <Navigate to="/gatherings" replace />;

  const { prev, next } = getAdjacent(slug);
  const gallery = gathering.gallery;
  const upcoming = gathering.status === "upcoming";

  const navLightbox = (dir) =>
    setLightbox((i) => (i === null ? i : (i + dir + gallery.length) % gallery.length));

  return (
    <main className="bg-gaia-bg" data-testid="gathering-detail-page">
      <Navigation />

      {/* Cinematic hero */}
      <section className="relative h-[62vh] min-h-[440px] w-full overflow-hidden bg-gaia-charcoal">
        <motion.img
          src={gathering.hero}
          alt={gathering.title}
          className="absolute inset-0 h-full w-full object-cover"
          initial={{ scale: 1.08, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 1.8, ease: EASE }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/20 to-black/70" />
        <div className="relative z-10 mx-auto flex h-full max-w-6xl flex-col justify-end px-6 pb-12 md:px-12 md:pb-16">
          <FadeUp>
            <span className="font-sans text-[11px] uppercase tracking-[0.34em] text-gaia-bg/80" data-testid="gathering-eyebrow">
              {upcoming ? "Upcoming · " : ""}{gathering.date} · {gathering.location}
            </span>
          </FadeUp>
          <motion.h1
            className="mt-5 max-w-4xl font-serif text-4xl font-light leading-[1.06] tracking-tight text-gaia-bg md:text-6xl"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.1, ease: EASE, delay: 0.2 }}
            data-testid="gathering-title"
          >
            {gathering.title}
          </motion.h1>
          {gathering.subtitle && (
            <motion.p
              className="mt-4 font-sans text-[11px] uppercase tracking-[0.34em] text-gaia-bg/70 md:text-xs"
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1.1, ease: EASE, delay: 0.4 }}
              data-testid="gathering-subtitle"
            >
              {gathering.subtitle}
            </motion.p>
          )}
        </div>
      </section>

      {/* Lead intro */}
      <section className="mx-auto max-w-2xl px-6 py-10 text-center md:py-12">
        {gathering.lead.map((p, i) => (
          <FadeUp key={i} delay={i * 0.1}>
            <p className="mb-4 font-serif text-lg font-light leading-relaxed text-gaia-charcoal last:mb-0 md:text-2xl">
              {p}
            </p>
          </FadeUp>
        ))}
      </section>

      {/* Upcoming: details + reserve */}
      {upcoming && (
        <section className="mx-auto max-w-3xl px-6 pb-14 text-center md:pb-16" data-testid="gathering-upcoming">
          {gathering.tagline && (
            <FadeUp>
              <p className="font-serif text-2xl font-light italic leading-snug tracking-tight text-gaia-charcoal md:text-4xl">
                {gathering.tagline}
              </p>
            </FadeUp>
          )}

          {/* Refined info panel — compact, intentional, legible */}
          <FadeUp delay={0.1}>
            <div
              className="mx-auto mt-9 overflow-hidden rounded-sm border border-gaia-charcoal/15 bg-white/55 text-left shadow-[0_20px_60px_-40px_rgba(46,43,41,0.5)]"
              data-testid="gathering-panel"
            >
              <div className="grid grid-cols-1 divide-y divide-gaia-charcoal/10 md:grid-cols-2 md:divide-x md:divide-y-0">
                <div className="px-7 py-8 md:px-9 md:py-9">
                  <span className="font-sans text-[10px] uppercase tracking-[0.3em] text-gaia-terracotta">
                    The evening
                  </span>
                  <ul className="mt-5 space-y-2.5">
                    {gathering.details.map((d, i) => (
                      <li
                        key={i}
                        className="font-serif text-lg font-light leading-snug text-gaia-charcoal/90 md:text-xl"
                      >
                        {d}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="px-7 py-8 md:px-9 md:py-9" data-testid="gathering-practical">
                  <span className="font-sans text-[10px] uppercase tracking-[0.3em] text-gaia-terracotta">
                    The details
                  </span>
                  <dl className="mt-5 space-y-4">
                    {gathering.practical?.map((row) => (
                      <div key={row.label}>
                        <dt className="font-sans text-[10px] uppercase tracking-[0.26em] text-gaia-charcoal/45">
                          {row.label}
                        </dt>
                        <dd className="mt-1 font-serif text-base font-light leading-snug text-gaia-charcoal/90 md:text-lg">
                          {row.value}
                        </dd>
                      </div>
                    ))}
                  </dl>
                </div>
              </div>
            </div>
          </FadeUp>

          <FadeUp delay={0.18}>
            <p className="mt-10 font-serif text-xl font-light italic leading-relaxed text-gaia-charcoal md:text-2xl">
              {gathering.invite}
            </p>
          </FadeUp>
          <FadeUp delay={0.24}>
            <button
              onClick={() => reserve(gathering)}
              className="group relative mt-6 overflow-hidden border border-gaia-charcoal px-10 py-4 font-sans text-[11px] uppercase tracking-[0.24em] text-gaia-charcoal"
              data-testid="gathering-reserve"
            >
              <span aria-hidden className="absolute inset-0 origin-bottom scale-y-0 bg-gaia-charcoal transition-transform duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-y-100" />
              <span className="relative z-10 block transition-colors duration-500 group-hover:text-gaia-bg">
                Reserve your space
              </span>
            </button>
          </FadeUp>
          {gathering.note && (
            <FadeUp delay={0.3}>
              <div className="mt-7 flex items-center justify-center gap-2.5 text-gaia-charcoal/45" data-testid="gathering-note">
                <Leaf size={13} strokeWidth={1.4} />
                <p className="font-sans text-xs uppercase tracking-[0.22em]">
                  {gathering.note}
                </p>
              </div>
            </FadeUp>
          )}
        </section>
      )}

      {/* Editorial masonry gallery */}
      {gallery.length > 0 && (
      <section className="mx-auto max-w-6xl px-6 pb-4 md:px-12" data-testid="gathering-gallery">
        <div className="columns-1 gap-5 sm:columns-2 lg:columns-3 [&>*]:mb-5">
          {gallery.map((img, i) => (
            <motion.button
              key={img.src}
              onClick={() => setLightbox(i)}
              className="group block w-full overflow-hidden rounded-sm bg-gaia-charcoal/5"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.8, ease: EASE }}
              data-testid={`gallery-item-${i}`}
              aria-label={`Open image ${i + 1}`}
            >
              <img
                src={img.src}
                alt={img.alt || ""}
                loading="lazy"
                className="w-full object-cover transition-transform duration-[1300ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.04]"
              />
            </motion.button>
          ))}
        </div>
      </section>
      )}

      {/* Optional video */}
      {gathering.video && (
        <section className="mx-auto max-w-5xl px-6 pt-10 md:px-12">
          <div className="aspect-video w-full overflow-hidden rounded-sm bg-gaia-charcoal">
            <video src={gathering.video} controls playsInline className="h-full w-full object-cover" />
          </div>
        </section>
      )}

      {/* Reflection */}
      {gathering.reflection.length > 0 && (
      <section className="mx-auto max-w-2xl px-6 py-16 text-center md:py-20">
        {gathering.reflection.map((p, i) => (
          <FadeUp key={i} delay={i * 0.08}>
            <p className="mb-4 font-serif text-xl font-light leading-relaxed text-gaia-charcoal/85 last:mb-0 md:text-2xl">
              {p}
            </p>
          </FadeUp>
        ))}
      </section>
      )}

      {/* Prev / next */}
      <section className="border-t border-gaia-charcoal/10">
        <div className="mx-auto grid max-w-6xl grid-cols-1 divide-y divide-gaia-charcoal/10 md:grid-cols-2 md:divide-x md:divide-y-0">
          <div className="px-6 py-10 md:px-12">
            {prev ? (
              <Link to={`/gatherings/${prev.slug}`} className="group flex flex-col" data-testid="gathering-prev">
                <span className="inline-flex items-center gap-2 font-sans text-[11px] uppercase tracking-[0.24em] text-gaia-taupe">
                  <ArrowLeft size={14} strokeWidth={1.5} className="transition-transform duration-500 group-hover:-translate-x-1.5" />
                  Previous
                </span>
                <span className="mt-2 font-serif text-xl font-light text-gaia-charcoal transition-colors duration-500 group-hover:text-gaia-terracotta">{prev.title}</span>
              </Link>
            ) : (
              <span className="font-sans text-[11px] uppercase tracking-[0.24em] text-gaia-charcoal/30">Beginning</span>
            )}
          </div>
          <div className="px-6 py-10 md:px-12 md:text-right">
            {next ? (
              <Link to={`/gatherings/${next.slug}`} className="group flex flex-col md:items-end" data-testid="gathering-next">
                <span className="inline-flex items-center gap-2 font-sans text-[11px] uppercase tracking-[0.24em] text-gaia-taupe">
                  Next
                  <ArrowRight size={14} strokeWidth={1.5} className="transition-transform duration-500 group-hover:translate-x-1.5" />
                </span>
                <span className="mt-2 font-serif text-xl font-light text-gaia-charcoal transition-colors duration-500 group-hover:text-gaia-terracotta">{next.title}</span>
              </Link>
            ) : (
              <Link to="/gatherings" className="group inline-flex flex-col md:items-end" data-testid="gathering-all">
                <span className="inline-flex items-center gap-2 font-sans text-[11px] uppercase tracking-[0.24em] text-gaia-taupe">
                  All Gatherings
                  <ArrowRight size={14} strokeWidth={1.5} className="transition-transform duration-500 group-hover:translate-x-1.5" />
                </span>
                <span className="mt-2 font-serif text-xl font-light text-gaia-charcoal">The living archive</span>
              </Link>
            )}
          </div>
        </div>
      </section>

      <Footer />

      <Lightbox images={gallery} index={lightbox} onClose={() => setLightbox(null)} onNav={navLightbox} />
    </main>
  );
}
